import "./OverrideMarkerPopup.css";

import React, { Fragment } from "react";
import { Divider } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { Popup } from "react-mapbox-gl";
import { Text } from "../../Base";
import { View } from "../../View";
import { palette } from "../../../theme/palette";
import { groupBy } from "lodash";
import { CloseIcon } from "../../Icons/Close";
import { WarningIcon } from "../../Icons/Warning";
import { timestampToHHMM } from "../../../utils/date";

const useStyles = makeStyles({
  popup: {
    color: palette.white,
    width: "245px",
    maxHeight: "280px",
    paddingBottom: "50px",
    paddingRight: "5px",
    zIndex: "4!important",
    borderColor: palette.typography.black.high,
    borderRadius: "10px",
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    "& .mapboxgl-popup-content": {
      background: "transparent",
      padding: "0px !important",
    },
    "&::-webkit-scrollbar": {
      display: "none",
    },
  },
  popupContents: {
    width: "240px",
    maxHeight: "255px",
    background: palette.lowTranslucentTrueBlack,
    padding: "10px",
    borderRadius: "10px",
  },
  detailsContainerTopRow: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    fontSize: "18px",
  },
  detailsContainerCloseIcon: {
    height: "12px",
    cursor: "pointer",
  },
  postcode: {
    fontSize: "19px",
  },
  route: {
    fontSize: "16px",
    marginTop: "10px",
    marginBottom: "0px",
    color: palette.sunburst,
  },
  overrideDetails: {
    display: "flex",
    marginBottom: "5px",
  },
  overrideListItem: {
    fontSize: "12px",
  },
  detailsContainerDivider: {
    background: palette.translucentWhite,
  },
  overrideList: {
    overflow: "scroll",
    margin: "0px",
    maxHeight: "240px",
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    "&::-webkit-scrollbar": {
      display: "none",
    },
  },
  iconContainer: {
    display: "flex",
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    marginLeft: "15px",
  },
  warningIcon: {
    width: "22px",
    height: "22px",
    fill: palette.dpdRed,
  },
});

export const OverrideMarkerPopup = ({
  showOverride,
  overrides,
  coordinates,
  overrideModalsToShow,
  handleOverrideModalChange,
}) => {
  const styles = useStyles();

  const processOverrideList = () => {
    let overrideList = [];
    const groupedOverrides = groupBy(overrides, "routeCode");
    overrideList = Object.keys(groupedOverrides).map((routeCode) => {
      const overridesForRouteCode = groupedOverrides[routeCode];
      let overrideListForRouteCode = {
        routeCode,
        overrideLines: [],
      };

      overridesForRouteCode.forEach((overrideForRouteCode) => {
        overrideListForRouteCode.overrideLines = [
          ...overrideListForRouteCode.overrideLines,
          overrideForRouteCode,
        ];
      });

      return overrideListForRouteCode;
    });
    return overrideList;
  };

  const renderOverrideList = (overrideList) => {
    const filterOverrideListByLevel = (level) => {
      let filteredOverrideList = [];
      overrideList.forEach((override) => {
        const lines = override.overrideLines.filter((overrideLine) => {
          const { outer, sector, postcode } = overrideLine;
          switch (level) {
            case "postcode":
              return postcode !== "";
            case "sector":
              return sector !== "" && postcode === "";
            case "outer":
            default:
              return outer !== "" && sector === "" && postcode === "";
          }
        });
        filteredOverrideList = [...filteredOverrideList, ...lines];
      });
      return filteredOverrideList;
    };

    const levelledOverrides = {
      outer: groupBy(filterOverrideListByLevel("outer"), "outer"),
      sector: groupBy(filterOverrideListByLevel("sector"), "sector"),
      postcode: groupBy(filterOverrideListByLevel("postcode"), "postcode"),
    };

    const renderModalTextForLevel = (level) => {
      return Object.keys(levelledOverrides[level]).flatMap((postcode) => {
        const groupedRoutes = groupBy(
          levelledOverrides[level][postcode],
          "routeCode"
        );
        const textForLevel = [
          <Text key={postcode} className={styles.postcode}>
            {postcode}
          </Text>,
          ...Object.keys(groupedRoutes).flatMap((routeCode) => [
            <Text
              key={`${postcode}-${routeCode}`}
              className={styles.route}
            >{`Route ${routeCode}`}</Text>,
            ...groupedRoutes[routeCode].map((override) => {
              const {
                overrideUuid,
                uiOverrideStartTime,
                uiOverrideEndTime,
                startOperator,
                endOperator,
                conflict,
                excludeOneHour,
              } = override;

              return (
                <View
                  key={`${postcode}-${routeCode}-${overrideUuid}`}
                  className={styles.overrideDetails}
                >
                  <View>
                    {uiOverrideStartTime && (
                      <Text className={styles.overrideListItem}>
                        {`Service Window Start Time: ${startOperator} ${timestampToHHMM(
                          uiOverrideStartTime
                        )}`}
                      </Text>
                    )}
                    {uiOverrideEndTime && (
                      <Text className={styles.overrideListItem}>
                        {`Service Window End Time: ${endOperator} ${timestampToHHMM(
                          uiOverrideEndTime
                        )}`}
                      </Text>
                    )}
                    {excludeOneHour && (
                      <Text className={styles.overrideListItem}>
                        Exclude One Hour Windows
                      </Text>
                    )}
                  </View>
                  {conflict && (
                    <View className={styles.iconContainer}>
                      <WarningIcon className={styles.warningIcon} />
                    </View>
                  )}
                </View>
              );
            }),
          ]),
        ];
        return (
          <Fragment key={postcode}>
            <View className={styles.detailsTextList}>{textForLevel}</View>
            <Divider className={styles.detailsContainerDivider} light={true} />
          </Fragment>
        );
      });
    };
    return ["outer", "sector", "postcode"].flatMap(renderModalTextForLevel);
  };

  const overrideList = processOverrideList();
  const { onClose } =
    overrideModalsToShow.find(
      ({ coordinates: modalCoordinates }) => modalCoordinates === coordinates
    ) || {};

  return (
    <View>
      {onClose && (
        <Popup
          coordinates={coordinates}
          anchor={"right"}
          offset={10}
          className={styles.popup}
        >
          <View className={styles.popupContents}>
            <View className={styles.detailsContainerTopRow}>
              <Text>Override</Text>
              <CloseIcon
                role="button"
                aria-pressed={showOverride}
                onClick={() => {
                  handleOverrideModalChange(false, { coordinates });
                  onClose();
                }}
                className={styles.detailsContainerCloseIcon}
              />
            </View>
            <Divider className={styles.detailsContainerDivider} light={true} />
            <View className={styles.overrideList}>
              {renderOverrideList(overrideList)}
            </View>
          </View>
        </Popup>
      )}
    </View>
  );
};
